import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { channelTitle, sortChannels, useChannels } from '../data/channels';
import { useUsers } from '../data/users';
import { useSession } from '../lib/session';
import { stagePendingShare, takeSharedItems, type SharedItems } from '../lib/share-inbox';
import { Dialog } from './Dialog';
import { Icon } from './Icon';

/** A cim jelzoje: a service worker ezzel nyitja meg az appot megosztas utan. */
const SHARE_PARAM = 'megosztas';

/**
 * Hova menjen a megosztott tartalom? (K2, lasd lib/share-inbox.ts)
 *
 * A valasztas utan a csatorna szerkesztojebe kerul - ELKULDENI a felhasznalo
 * kuldi el, igy meg hozzairhat vagy meggondolhatja magat.
 */
export function ShareTargetDialog() {
  const navigate = useNavigate();
  const [items, setItems] = useState<SharedItems | null>(null);
  const [query, setQuery] = useState('');
  const { data: channels } = useChannels();
  const { usersById, nameDisplay } = useUsers();
  const meId = useSession().data?.user.id ?? '';

  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(SHARE_PARAM)) return;
    // A jelzot rogton levesszuk: ujratoltes vagy vissza-lepes ne nyissa meg ujra.
    url.searchParams.delete(SHARE_PARAM);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    void takeSharedItems().then((shared) => {
      if (shared && (shared.text || shared.files.length > 0)) setItems(shared);
    });
  }, []);

  const entries = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('hu');
    return sortChannels(channels ?? [])
      .filter((channel) => !channel.isArchived)
      .map((channel) => ({
        id: channel.id,
        label: channelTitle(channel, usersById, meId, nameDisplay),
      }))
      .filter((entry) => !needle || entry.label.toLocaleLowerCase('hu').includes(needle));
  }, [channels, usersById, meId, nameDisplay, query]);

  if (!items) return null;

  const summary = [
    items.files.length > 0 ? `${items.files.length} fájl` : null,
    items.text ? `„${items.text.slice(0, 60)}${items.text.length > 60 ? '…' : ''}”` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && setItems(null)}
      title="Megosztás az AVSlackben"
      description={summary}
    >
      <input
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Csatorna vagy kolléga keresése"
        className="mb-2 w-full rounded-control border border-border bg-surface px-3 py-2 text-sm"
      />
      <ul className="max-h-[calc(var(--app-height,100dvh)*0.5)] overflow-y-auto">
        {entries.map((entry) => (
          <li key={entry.id}>
            <button
              type="button"
              onClick={() => {
                stagePendingShare(entry.id, items);
                setItems(null);
                void navigate({ to: '/c/$channelId', params: { channelId: entry.id } });
              }}
              className="flex w-full items-center gap-2 rounded-control px-2 py-2 text-left text-sm hover:bg-surface-hover"
            >
              <Icon name="csatorna" size="sm" />
              <span className="min-w-0 truncate">{entry.label}</span>
            </button>
          </li>
        ))}
        {entries.length === 0 && (
          <li className="px-2 py-2 text-sm text-ink-muted">Nincs találat.</li>
        )}
      </ul>
    </Dialog>
  );
}
